import { useEffect } from "react";
import {
  changeCheckBeatMs,
  notifyShowMoved,
  subscribeNudgeStore,
} from "common-fe/changeNudge";
import { DEFAULT_POLL_MS, MIN_POLL_MS } from "../local/changeCheck";

// card #3200 - the timer around sync.js's checkShowChanges(). Asks the change
// endpoint for the active show every `pollMs` (the server's setting, default
// 5000, learned from each answer) and nothing else:
//   - only while `online` and the page is on screen (a hidden tab stops asking
//     and asks once, at once, when it comes back);
//   - the FIRST ask waits a random offset in [0, pollMs) so a ring of tablets
//     that powered on together does not hit the API in lockstep;
//   - the next ask is scheduled when the previous one SETTLES, so a slow
//     network can never stack requests;
//   - a failed ask changes nothing: the screens keep painting from the cache
//     (docs/score/scribe-resilience.md I8) and the next tick just asks again.
//
// Change nudges (card #3562, docs/adr/0003). The staff WebSocket (mounted once in
// ScoreContext) writes into a store this hook reads: a NUDGE runs a check at
// once, and while the socket is HEALTHY the next ask is 30 s out instead of
// pollMs (the nudge is the fast path, this only backstops it). The moment the
// socket drops the beat is pollMs again and a check runs right then. A check
// that pulled something tells the store, so a socket released by Instant updates
// OFF can ask again whether it is ON now. Nothing here is shown to the scribe.
//
// `random` is injectable so a test can pin the offset.
export function useShowChangeCheck({
  showId,
  online,
  engine,
  random = Math.random,
}) {
  useEffect(() => {
    if (!showId || !online || !engine) return undefined;

    let cancelled = false;
    let timer = null;
    let controller = null;
    let running = false;
    // a nudge landed mid-check: that check may have read before the write, so
    // one more runs as soon as it settles
    let again = false;
    let pollMs = DEFAULT_POLL_MS;

    const clearTimer = () => {
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const hidden = () => document.visibilityState === "hidden";

    const schedule = (ms) => {
      clearTimer();
      if (cancelled || hidden()) return;
      timer = setTimeout(tick, ms);
    };

    async function tick() {
      timer = null;
      if (cancelled || hidden() || running) return;
      running = true;
      controller = new AbortController();
      let next = pollMs;
      try {
        const result = await engine.checkShowChanges(showId, controller.signal);
        if (result?.pollMs) {
          pollMs = result.pollMs;
          next = changeCheckBeatMs(showId, pollMs);
          if (result.pulledClasses?.length || result.pulledJudges) notifyShowMoved(showId);
        } else if (result == null) {
          // Nothing asked (a hydrate/sync is mid-walk and seeds the baselines
          // itself): look again soon instead of waiting out a whole interval.
          next = Math.min(pollMs, MIN_POLL_MS);
        }
      } catch {
        // endpoint unreachable: keep the cache, ask again next tick
      } finally {
        running = false;
        controller = null;
        if (again && !cancelled) {
          again = false;
          next = 0;
        }
        schedule(next);
      }
    }

    const onVisibility = () => {
      if (cancelled) return;
      if (hidden()) {
        clearTimer();
        controller?.abort();
      } else if (!running) {
        // back on screen: catch up now rather than after a full interval
        clearTimer();
        void tick();
      }
    };

    schedule(Math.floor(random() * pollMs));
    document.addEventListener("visibilitychange", onVisibility);
    const unsubscribeNudges = subscribeNudgeStore(showId, (event) => {
      if (cancelled || hidden()) return;
      if (event.kind === "nudge") {
        if (running) {
          again = true;
        } else {
          clearTimer();
          void tick();
        }
      } else if (event.healthy) {
        // healthy: slow the next ask to 30 s (unless a check is running; its
        // settle schedules with the new beat)
        if (!running && timer != null) schedule(changeCheckBeatMs(showId, pollMs));
      } else if (!running) {
        // down: the normal rate again, and catch up on what the socket may have missed
        clearTimer();
        void tick();
      }
    });
    return () => {
      cancelled = true;
      unsubscribeNudges();
      clearTimer();
      controller?.abort();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [showId, online, engine, random]);
}

export default useShowChangeCheck;
